import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { CheckCircle2, ChevronDown, MessageCircle } from "lucide-react";
import Navbar from "./Navbar";
import Footer from "./Footer";

// Objections finales — titres + réponses textuelles fournies par le client.
// Les réponses vidéo remplaceront les textes plus tard.
const OBJECTIONS = [
  {
    key: "obj-6",
    title: "« Je poste déjà du contenu. Qu'est-ce que vous m'apportez de plus ? »",
    teaser: "Plus qu'une production : une vraie stratégie de contenu pensée pour tes résultats long terme.",
    videoUrl: "https://customer-assets-7cd3h4nn.emergentagent.net/job_agency-portal-139/artifacts/iqqcux66_OBJ%20VSL%207.mp4",
    answer: [
      "C'est simple. Nous ne t'aidons pas seulement à publier plus de contenu. Nous t'aidons à obtenir plus de résultats.",
      "Car aujourd'hui, tu publies sûrement du contenu, mais avec des résultats irréguliers, et parfois même dans le vide.",
      "C'est justement là que notre rôle intervient. Nous mettons en place une véritable stratégie de contenu, avec des concepts adaptés à ton activité et une vision long terme, afin d'obtenir des résultats plus réguliers et de construire une croissance durable.",
    ],
  },
  {
    key: "obj-1",
    title: "« Je n'aurais jamais le temps de tourner 30 vidéos par mois »",
    teaser: "Seulement 5h par mois de ton temps. On s'occupe de tout le reste — stratégie, tournage, montage.",
    videoUrl: "https://customer-assets-7cd3h4nn.emergentagent.net/job_agency-portal-139/artifacts/d3z5e2f3_OBJ%20VSL%206.mp4",
    answer: [
      "Si tu te poses cette question, c'est sûrement que tu as une vie professionnelle et personnelle très active.",
      "C'est justement pour ça que nous avons créé une méthode qui te permet de produire jusqu'à 30 vidéos par mois en seulement 5 heures de ton temps.",
      "Concrètement, cela représente simplement : 2 tournages de 2 heures — dans tes bureaux ou dans nos studios podcast — ainsi qu'un appel stratégique d'une heure. Lors de cet appel, nous te présentons les concepts, les angles et les idées de contenus afin de les valider ensemble.",
      "Une fois cette étape terminée, tu n'as plus rien d'autre à faire. Notre équipe s'occupe du reste, de la stratégie jusqu'au montage, pour que tu puisses te concentrer sur ton activité.",
    ],
  },
  {
    key: "obj-2",
    title: "« Comment faites-vous pour vous adapter à n'importe quelle niche ? »",
    teaser: "Notre méthode a déjà fait performer restauration, podcast, immobilier, coaching. B2B comme B2C.",
    videoUrl: "https://customer-assets-7cd3h4nn.emergentagent.net/job_agency-portal-139/artifacts/mebp92a1_OBJ%20VSL%201.mp4",
    answer: [
      "C'est simple. Notre promesse est de t'obtenir des résultats, peu importe ta niche.",
      "Et si nous pouvons nous permettre de dire ça avec autant de sérénité, c'est parce que nous l'avons déjà prouvé. Nous avons fait performer des grands groupes de restauration comme Nach, des studios de podcast comme Podmax, des agents immobiliers, et même des coachs en ligne en B2B.",
      "Si notre méthode marche peu importe ton marché, c'est parce qu'on t'écoute vraiment sur la personne que tu es et la cible que tu vises, afin de te créer une image de marque sur mesure qui convertit — peu importe ta niche.",
    ],
  },
  {
    key: "obj-5",
    title: "« Est-ce que je vais être pris au sérieux dans mon contenu ? »",
    teaser: "Studios premium + montage pro. Tu deviens immédiatement crédible aux yeux de ton audience.",
    videoUrl: "https://customer-assets-7cd3h4nn.emergentagent.net/job_agency-portal-139/artifacts/xtq1yqth_OBJ%20VSL%208.mp4",
    answer: [
      "Évidemment. Pour nous, c'est même l'une des choses les plus importantes.",
      "Car notre objectif est que tu deviennes la référence de ton marché.",
      "C'est pour cela que tu bénéficies de studios premium durant tes tournages, ainsi que d'un montage professionnel — pour que tu sois immédiatement crédible aux yeux de ton audience.",
    ],
  },
  {
    key: "obj-8",
    title: "« Je n'ai pas un problème d'acquisition, mais un problème de conversion »",
    teaser: "Notre méthode Attirer · Éduquer · Convertir transforme les vues en leads qualifiés, pas juste en vanity metrics.",
    videoUrl: "https://customer-assets-7cd3h4nn.emergentagent.net/job_agency-portal-139/artifacts/zmlao91v_OBJ%20VSL%204.mp4",
    answer: [
      "Je comprends. Et c'est un problème que beaucoup d'entrepreneurs rencontrent aujourd'hui, car ils se concentrent uniquement sur la viralité et pas sur la conversion. Résultat : ils font beaucoup de vues, mais 0 ventes.",
      "C'est pour ça que chez Elevate Influence, nous avons créé la méthode : Attirer — Éduquer — Convertir.",
      "Le but est simple. Créer des vidéos qui vont te ramener du trafic pour te faire connaître, d'autres vidéos pour éduquer ton audience et partager ton expertise et ta vision, puis des vidéos de conversion pour transformer cette audience en prospects.",
      "Comme ça, tu as un système équilibré où tu génères des leads — mais surtout des leads qualifiés grâce à ton contenu — afin d'améliorer ta conversion.",
    ],
  },
  {
    key: "obj-7",
    title: "« Je n'ai jamais tourné avec une caméra, je ne sais pas si je me sens prêt »",
    teaser: "On t'accompagne de A à Z : prise de parole, reformulation, mise à l'aise. Tu montes en confiance à chaque tournage.",
    videoUrl: "https://customer-assets-7cd3h4nn.emergentagent.net/job_agency-portal-139/artifacts/30p1n27a_OBJ%20VSL%203.mp4",
    answer: [
      "Je comprends. Et c'est justement l'avantage de travailler avec nous : nous t'accompagnons de A à Z.",
      "Pendant le tournage, nous t'aidons dans ta prise de parole afin que tu puisses parler de ton expertise de la manière la plus naturelle possible. Nous reformulons tes phrases si nécessaire et nous te guidons tout au long du processus.",
      "Mais surtout, nous prenons le temps durant le tournage de te mettre à l'aise, afin que tu gagnes progressivement en confiance et que l'exercice devienne de plus en plus naturel.",
    ],
  },
  {
    key: "obj-3",
    title: "« Est-ce que je vais devoir jouer un rôle dans mon contenu ? »",
    teaser: "Aucun rôle à jouer. On construit une image de marque qui valorise ta personnalité et ton expertise.",
    videoUrl: "https://customer-assets-7cd3h4nn.emergentagent.net/job_agency-portal-139/artifacts/vxlelqpb_OBJ%20VSL%202.mp4",
    answer: [
      "Notre objectif n'est pas de te faire jouer un personnage ou de te faire copier tes concurrents. Non, pas du tout.",
      "Notre objectif est de te créer une image de marque qui valorise ton expertise et ta personnalité, afin qu'elle te corresponde réellement et qu'elle soit unique.",
      "Car aujourd'hui, dans un marché aussi concurrentiel, ce ne sont pas les personnes qui jouent un rôle qui réussissent, mais celles qui ont une image de marque différenciante.",
    ],
  },
  {
    key: "obj-4",
    title: "« Qu'est-ce qui vous différencie des autres agences ? »",
    teaser: "Une approche 100% sur mesure. On ne duplique jamais la même image de marque pour deux clients.",
    videoUrl: "https://customer-assets-7cd3h4nn.emergentagent.net/job_agency-portal-139/artifacts/4c2953w9_OBJ%20VSL%205.mp4",
    answer: [
      "La particularité de notre agence, c'est que nous avons une approche complètement sur mesure.",
      "Le but n'est pas de te créer la même image de marque et les mêmes concepts que tous nos autres clients. Au contraire, nous cherchons à créer ton élément de différenciation.",
      "Si nous pensons que le format facecam est ce qu'il y a de mieux pour toi, alors nous le ferons. Et si nous voyons que le podcast ne te correspond pas, nous ne te forcerons jamais à en faire. Car le but est de créer des concepts qui te correspondent réellement.",
      "Chez Elevate Influence, nous partons du principe que pour obtenir de bons résultats, tu dois être fier du contenu que tu publies.",
    ],
  },
];

// WhatsApp — numéro Hugo Roussell (+33 6 24 75 57 74) au format wa.me.
const WHATSAPP_LINK =
  "https://wa.me/33624755774?text=" +
  encodeURIComponent(
    "Salut Hugo, j'ai réservé un call sur iClosed et j'aurais une question avant le rendez-vous."
  );

export default function ThankYou() {
  const [openKey, setOpenKey] = useState(OBJECTIONS[0].key); // first one open by default

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <main data-testid="thankyou-page" className="mx-auto max-w-5xl px-6 pb-24 pt-32 sm:pt-40">
        {/* Confirmation banner */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
          data-testid="booking-confirmation"
          className="flex flex-col items-center text-center"
        >
          <span className="mb-5 flex h-16 w-16 items-center justify-center rounded-full bg-[#2563EB]/10 ring-8 ring-[#2563EB]/5">
            <CheckCircle2 size={32} className="text-[#2563EB]" strokeWidth={2.4} />
          </span>
          <p className="text-[11px] font-bold uppercase tracking-[0.24em] text-[#2563EB]">
            Réservation confirmée
          </p>
          <h1 className="font-head mt-4 max-w-3xl text-4xl font-extrabold text-[#0B1220] sm:text-5xl">
            Bravo, ton <span className="text-[#2563EB]">call</span> est réservé.
          </h1>
          <p className="mt-4 max-w-xl text-base text-[#5B6B85] sm:text-lg">
            En attendant l'appel, on a préparé pour toi les réponses aux questions qu'on nous pose le plus souvent.
          </p>
        </motion.div>

        {/* Objections accordion */}
        <div className="mt-16" data-testid="objections-list">
          <p className="mb-6 text-center text-[11px] font-bold uppercase tracking-[0.24em] text-[#5B6B85]">
            Les réponses aux questions qu'on nous pose le plus
          </p>

          <div className="space-y-3">
            {OBJECTIONS.map((o, i) => {
              const isOpen = openKey === o.key;
              return (
                <motion.div
                  key={o.key}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: i * 0.05 }}
                  data-testid={`objection-${i}`}
                  className={`overflow-hidden rounded-2xl border transition-colors duration-300 ${
                    isOpen
                      ? "border-[#2563EB]/40 bg-white shadow-[0_18px_48px_-24px_rgba(37,99,235,0.35)]"
                      : "border-[#E6EDF6] bg-white hover:border-[#2563EB]/25"
                  }`}
                >
                  <button
                    type="button"
                    onClick={() => setOpenKey(isOpen ? null : o.key)}
                    aria-expanded={isOpen}
                    data-testid={`objection-toggle-${i}`}
                    className="flex w-full items-center justify-between gap-4 p-5 text-left sm:p-6"
                  >
                    <div className="flex flex-1 items-start gap-4">
                      <span
                        className={`mt-0.5 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full text-xs font-extrabold transition-colors duration-300 ${
                          isOpen
                            ? "bg-[#2563EB] text-white"
                            : "bg-[#F2F6FB] text-[#2563EB]"
                        }`}
                      >
                        {String(i + 1).padStart(2, "0")}
                      </span>
                      <div>
                        <h3 className="font-head text-lg font-bold text-[#0B1220] sm:text-xl">
                          {o.title}
                        </h3>
                        <p className="mt-1 text-sm text-[#5B6B85]">{o.teaser}</p>
                      </div>
                    </div>
                    <ChevronDown
                      size={22}
                      className={`flex-shrink-0 text-[#2563EB] transition-transform duration-300 ${
                        isOpen ? "rotate-180" : ""
                      }`}
                    />
                  </button>

                  <AnimatePresence initial={false}>
                    {isOpen && (
                      <motion.div
                        key="body"
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
                        className="overflow-hidden"
                      >
                        <div className="border-t border-[#E6EDF6] p-5 sm:p-6" data-testid={`objection-body-${i}`}>
                          {o.videoUrl ? (
                            <div className="overflow-hidden rounded-xl bg-black">
                              <video
                                src={o.videoUrl}
                                controls
                                playsInline
                                preload="metadata"
                                data-testid={`objection-video-${i}`}
                                className="aspect-video w-full"
                              />
                            </div>
                          ) : (
                            <div className="space-y-5 pl-0 sm:pl-12">
                              {o.answer.map((p, idx) => (
                                <p
                                  key={idx}
                                  className={`text-[15px] leading-[1.75] sm:text-base ${
                                    idx === 0 ? "font-semibold text-[#0B1220]" : "text-[#334155]"
                                  }`}
                                >
                                  {p}
                                </p>
                              ))}
                            </div>
                          )}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              );
            })}
          </div>
        </div>

        {/* WhatsApp CTA */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          data-testid="whatsapp-cta"
          className="mt-20 overflow-hidden rounded-3xl border border-[#25D366]/25 bg-gradient-to-br from-[#F2F6FB] to-white p-8 text-center sm:p-12"
        >
          <span className="inline-flex h-14 w-14 items-center justify-center rounded-full bg-[#25D366]/10 ring-8 ring-[#25D366]/5">
            <MessageCircle size={26} className="text-[#25D366]" />
          </span>
          <h2 className="font-head mt-5 text-2xl font-extrabold text-[#0B1220] sm:text-3xl">
            Une autre question avant le call&nbsp;?
          </h2>
          <p className="mx-auto mt-3 max-w-xl text-[#5B6B85]">
            Écris-moi directement sur WhatsApp — je te réponds personnellement en moins de 2h.
          </p>
          <a
            href={WHATSAPP_LINK}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="whatsapp-cta-button"
            className="mt-8 inline-flex items-center gap-2.5 rounded-full bg-[#25D366] px-8 py-4 text-base font-semibold text-white shadow-[0_18px_40px_-14px_rgba(37,211,102,0.55)] transition-transform duration-300 hover:scale-[1.02]"
          >
            <MessageCircle size={20} />
            Envoie-moi un message WhatsApp
          </a>
        </motion.div>

        <div className="mt-14 text-center">
          <Link
            to="/"
            data-testid="thankyou-back-home"
            className="text-sm font-semibold text-[#5B6B85] underline decoration-[#2563EB]/40 underline-offset-4 transition-colors hover:text-[#2563EB]"
          >
            ← Retour à l'accueil
          </Link>
        </div>
      </main>
      <Footer />
    </div>
  );
}
